/**
 * Match creation: players from lobby slots, region assignment, start packages (§1.6).
 */
import { BALANCE } from './balance.js';
import { addStructure, capEff, refreshCityMult, setCityLevel } from './entities.js';
import { axialCenter, getGeo, hexDist } from './hex.js';
import { rngState, streamSeed } from './rng.js';
import { initStats } from './systems/stats.js';
import { computeVision, createVisionState } from './systems/vision.js';
import { refreshAggregates } from './systems/scoring.js';
import type { CityEntity, HexIdx, MatchSettings, MatchState, PlayerSlot, PlayerState, RegionId, WorldMap } from './types.js';

/** Fallback seed regions for maps without the curated sets (ATLANTIC, EUROPA, CHINA, GANGES). */
export const SEED_REGIONS: readonly RegionId[] = [1, 4, 8, 7];

/**
 * The curated start regions of an n-player match (BALANCE.regionSets, §1.14), as region ids of
 * this map, or null when there is no set for n (8 players) or the map lacks one of its regions.
 */
export function regionSetFor(world: WorldMap, n: number): RegionId[] | null {
  const set = BALANCE.regionSets[n];
  if (!set) return null;
  const ids: RegionId[] = [];
  for (const name of set.regions) {
    const r = world.regions.find((g) => g.name === name);
    if (!r) return null;
    ids.push(r.id);
  }
  return ids;
}

/**
 * Start regions per team (index = rank of the team id, ascending) for a teams match with this
 * many players and teams (`BALANCE.teamLayouts`), or null when there is no layout or the map
 * lacks one of its regions.
 */
export function teamLayoutFor(world: WorldMap, players: number, teamCount: number): RegionId[][] | null {
  const layout = BALANCE.teamLayouts[`${players}x${teamCount}`];
  if (!layout) return null;
  const out: RegionId[][] = [];
  for (const group of layout) {
    const ids: RegionId[] = [];
    for (const name of group) {
      const r = world.regions.find((g) => g.name === name);
      if (!r) return null;
      ids.push(r.id);
    }
    out.push(ids);
  }
  return out;
}

/** Team layout regions for these seats (slot order within each team), or null if it does not apply (sizes differ). */
function layoutRegions(world: WorldMap, settings: MatchSettings, seats: PlayerSlot[]): RegionId[] | null {
  if (settings.mode !== 'teams') return null;
  const teamIds = [...new Set(seats.map((s) => s.team))].sort((a, b) => a - b);
  const layout = teamLayoutFor(world, seats.length, teamIds.length);
  if (!layout) return null;
  const members = teamIds.map((t) => seats.filter((s) => s.team === t));
  if (members.some((m, i) => m.length !== layout[i].length)) return null;
  const out: RegionId[] = new Array(seats.length);
  members.forEach((m, i) => m.forEach((s, k) => (out[seats.indexOf(s)] = layout[i][k])));
  return out;
}

/** Capacity scale of a start region in an n-player match: the set's table, else the 8-player one. */
/**
 * A lobby where at least half the seats are hard bots plays the hard capScale tables
 * (`capScaleHard`): hard and normal bots fight different wars, so one table cannot be fair to both.
 */
export function isHardLobby(seats: readonly { difficulty?: string }[]): boolean {
  return seats.length > 0 && 2 * seats.filter((s) => s.difficulty === 'hard').length >= seats.length;
}

export function capScaleFor(world: WorldMap, n: number, regionId: RegionId, hardLobby = false): number {
  const name = world.regions.find((r) => r.id === regionId)?.name ?? '';
  const set = BALANCE.regionSets[n];
  if (hardLobby) {
    const hard = set ? set.capScaleHard?.[name] : BALANCE.regions.capScaleHard[name];
    if (hard !== undefined) return hard;
  }
  return set?.capScale[name] ?? BALANCE.regions.capScale[name] ?? 1;
}

/**
 * Team layout capacity multiplier (`BALANCE.teamCapMult`) of a player in region `regionId`, or 1
 * when the players do not play a team layout (FFA, other team shapes, regions off the layout).
 * Works from public info (team + region per player), so bots and clients compute the same value.
 */
export function teamCapMult(world: WorldMap, players: readonly { team: number; regionId: RegionId }[], regionId: RegionId): number {
  const teamIds = [...new Set(players.map((p) => p.team))].sort((a, b) => a - b);
  const mult = BALANCE.teamCapMult[`${players.length}x${teamIds.length}`];
  const layout = teamLayoutFor(world, players.length, teamIds.length);
  if (!mult || !layout) return 1;
  for (let k = 0; k < teamIds.length; k++) {
    const regions = players.filter((p) => p.team === teamIds[k]).map((p) => p.regionId);
    if (regions.length !== layout[k].length || !layout[k].every((r) => regions.includes(r))) return 1;
  }
  return mult[layout.findIndex((g) => g.includes(regionId))] ?? 1;
}

/** A player's capacity scale: the region table (`capScaleFor`) × the team layout multiplier (`teamCapMult`). */
export function playerCapScale(
  world: WorldMap,
  players: readonly { team: number; regionId: RegionId; difficulty?: string }[],
  regionId: RegionId,
): number {
  return capScaleFor(world, players.length, regionId, isHardLobby(players)) * teamCapMult(world, players, regionId);
}

/**
 * Deterministic region assignment: lobby picks first (the lobby restricts them to the region
 * set), then the n-player region set in order, then the seed regions, then farthest-point
 * selection (max of min anchor distance, ties → lower id). One region per player, input order.
 */
export function assignRegions(world: WorldMap, picks: (RegionId | null | undefined)[]): RegionId[] {
  const ids = world.regions.map((r) => r.id);
  const anchor = new Map(world.regions.map((r) => [r.id, r.anchor]));
  const n = picks.length;
  if (n > ids.length) throw new Error(`map has ${ids.length} regions, need ${n}`);
  const out: (RegionId | 0)[] = new Array(n).fill(0);
  const used = new Set<RegionId>();
  picks.forEach((p, i) => {
    if (p != null && ids.includes(p) && !used.has(p)) {
      out[i] = p;
      used.add(p);
    }
  });
  const set = regionSetFor(world, n) ?? [];
  const pickNext = (): RegionId => {
    for (const s of set) if (!used.has(s)) return s;
    for (const s of SEED_REGIONS) {
      if (used.size < Math.min(n, SEED_REGIONS.length) && ids.includes(s) && !used.has(s)) return s;
    }
    let best: RegionId = 0;
    let bestD = -1;
    for (const id of [...ids].sort((a, b) => a - b)) {
      if (used.has(id)) continue;
      const a = anchor.get(id)!;
      let dmin = Infinity;
      for (const u of used) {
        const b = anchor.get(u)!;
        dmin = Math.min(dmin, (a.x - b.x) ** 2 + (a.y - b.y) ** 2);
      }
      if (dmin > bestD) {
        bestD = dmin;
        best = id;
      }
    }
    return best;
  };
  for (let i = 0; i < n; i++) {
    if (out[i]) continue;
    const r = pickNext();
    out[i] = r;
    used.add(r);
  }
  return out as RegionId[];
}

/** Occupied slots (human / bot) in slot order. */
export function occupiedSlots(slots: PlayerSlot[]): PlayerSlot[] {
  return [...slots].filter((s) => s.kind === 'human' || s.kind === 'bot').sort((a, b) => a.slot - b.slot);
}

/**
 * Create the authoritative state for a match. Players get ids 1..n in slot order.
 */
export function createMatch(world: WorldMap, settings: MatchSettings, seed: number, slots: PlayerSlot[]): MatchState {
  const geo = getGeo(world);
  const N = world.landCount;
  const seats = occupiedSlots(slots);
  const hardLobby = isHardLobby(seats);
  if (seats.length < 1 || seats.length > 8) throw new Error(`need 1..8 players, got ${seats.length}`);
  // teams play a measured team layout (lobby picks do not apply); FFA honours picks within the region set
  const regions =
    layoutRegions(world, settings, seats) ??
    assignRegions(
      world,
      seats.map((s) => s.regionId),
    );

  const players: PlayerState[] = seats.map((s, i) => ({
    id: i + 1,
    name: s.name,
    team: settings.mode === 'teams' ? s.team : i + 1,
    regionId: regions[i],
    isBot: s.kind === 'bot',
    ...(s.difficulty ? { difficulty: s.difficulty } : {}),
    ...(s.userId ? { userId: s.userId } : {}),
    capScale: capScaleFor(world, seats.length, regions[i], hardLobby),
    capitalHex: world.regions.find((r) => r.id === regions[i])!.capitalHex,
    capitalCriticalFrom: -1,
    capitalCriticalUntil: -1,
    front: { status: 'none', ratio: 0, rate: 0 },
    credits: BALANCE.economy.startCredits,
    incomePerMin: 0,
    focusHex: -1,
    focusSetTick: -1_000_000,
    eliminatedTick: -1,
    surrendered: false,
    hexCount: 0,
    cityCount: 0,
    areaW: 0,
    pop: 0,
    areaShare: 0,
    popShare: 0,
    killShare: 0,
    score: 0,
  }));
  // a team layout's per-team capacity multiplier (1 in FFA)
  for (const p of players) p.capScale *= teamCapMult(world, players, p.regionId);

  const state: MatchState = {
    world,
    geo,
    settings,
    seed: seed >>> 0,
    matchTicks: settings.matchTicks ?? BALANCE.matchTicks,
    tick: 0,
    players,
    owner: new Uint8Array(N),
    pop: new Float32Array(N),
    rad: new Float32Array(N),
    prog: new Float32Array(N),
    progBy: new Uint8Array(N),
    flipTick: new Int32Array(N).fill(-1_000_000),
    structId: new Int32Array(N).fill(-1),
    cityLvl: new Uint8Array(N),
    frontier: new Uint8Array(N),
    focusDist: new Uint8Array(N).fill(255),
    cityMult: new Float32Array(N).fill(1),
    entities: new Map(),
    nextId: 1,
    nextHintId: 1,
    nextDetonationId: 1,
    combatRng: rngState(streamSeed(seed, 'combat')),
    alliances: {
      allyOf: new Array(players.length + 1).fill(0),
      requests: [],
      betrayals: [],
      noAllyUntil: new Array(players.length + 1).fill(0),
      ceasefires: [],
      ceasefireRequests: [],
      noCeasefireUntil: new Array(players.length + 1).fill(0),
      markReadyTick: new Array(players.length + 1).fill(0),
    },
    vision: createVisionState(N, players.length),
    sensorsDirty: 0,
    domination: { holder: 0, sinceTick: 0 },
    stats: initStats(players),
    result: null,
  };

  // territory
  const regionOf = new Map(world.regions.map((r) => [r.id, r]));
  for (const p of players) {
    const reg = regionOf.get(p.regionId)!;
    for (const h of reg.coreHexes) state.owner[h] = p.id;
    for (let h = 0; h < N; h++) if (world.coreOf[h] === p.regionId) state.owner[h] = p.id;
  }

  // cities: map cities (neutral prizes or inside cores), then capitals / towns of used regions
  const used = new Set(players.map((p) => p.regionId));
  const placeCity = (hex: HexIdx, level: number, name: string): CityEntity => {
    const existing = state.structId[hex];
    if (existing >= 0) state.entities.delete(existing);
    const c = addStructure(state, 'city', state.owner[hex], hex, 0, name) as CityEntity;
    setCityLevel(state, c, level);
    return c;
  };
  for (const c of world.cities) {
    if (c.region && used.has(c.region)) continue; // used cores get the standard start package
    placeCity(c.hex, Math.max(1, Math.min(BALANCE.city.maxLevel, c.level)), c.name);
  }
  const cityName = (hex: HexIdx, fallback: string) => world.cities.find((c) => c.hex === hex)?.name ?? fallback;
  for (const p of players) {
    const reg = regionOf.get(p.regionId)!;
    placeCity(reg.capitalHex, BALANCE.start.capitalLevel, cityName(reg.capitalHex, reg.name));
    reg.townHexes.forEach((h, i) => placeCity(h, BALANCE.start.townLevel, cityName(h, `${reg.name.slice(0, 8)} ${i + 1}`)));
  }
  for (let h = 0; h < N; h++) if (state.cityLvl[h]) refreshCityMult(state, h);

  // population
  for (let h = 0; h < N; h++) {
    state.pop[h] = capEff(state, h) * (state.owner[h] ? BALANCE.pop.startFill : BALANCE.pop.neutralFrac);
  }

  // start structures: radar next to the capital, SAM triangle around it
  for (const p of players) placeStartStructures(state, p);

  refreshAggregates(state);
  computeVision(state, []);
  return state;
}

function placeStartStructures(state: MatchState, p: PlayerState): void {
  const w = state.world;
  const reg = w.regions.find((r) => r.id === p.regionId)!;
  const cap = reg.capitalHex;
  const free = (h: HexIdx) => h >= 0 && state.owner[h] === p.id && state.structId[h] < 0;
  // owned hexes sorted by distance from the capital, ties by index
  const cands: HexIdx[] = [];
  for (let h = 0; h < w.landCount; h++) if (state.owner[h] === p.id) cands.push(h);
  cands.sort((a, b) => hexDist(w, cap, a) - hexDist(w, cap, b) || a - b);

  for (let i = 0; i < BALANCE.start.radars; i++) {
    const h = cands.find((x) => free(x) && hexDist(w, cap, x) >= 1);
    if (h !== undefined) addStructure(state, 'radar', p.id, h, 0);
  }
  const ring = BALANCE.start.samRing;
  for (let i = 0; i < BALANCE.start.sams; i++) {
    // ideal point on a triangle around the capital
    const ang = -Math.PI / 2 + (i * 2 * Math.PI) / BALANCE.start.sams;
    const [cx, cy] = axialCenter(state.geo, w.q[cap], w.r[cap]);
    const rr = ring * state.geo.R * Math.sqrt(3);
    const ix = cx + Math.cos(ang) * rr;
    const iy = cy + Math.sin(ang) * rr;
    let best = -1;
    let bestD = Infinity;
    for (const h of cands) {
      if (!free(h) || hexDist(w, cap, h) < 1) continue;
      const d = (w.cx[h] - ix) ** 2 + (w.cy[h] - iy) ** 2;
      if (d < bestD) {
        bestD = d;
        best = h;
      }
    }
    if (best >= 0) addStructure(state, 'sam', p.id, best, 0);
  }
}
